/**
 * Reading the browser's own state for Ask Inventree.
 *
 * Ask answers three kinds of question from the database — a list, a measure, a
 * report — and a fourth from the machine the question was typed on: the Dexie
 * caches, the Pinia stores, localStorage and the cookie jar. That fourth one
 * cannot be answered on the server, because none of those four exist there.
 *
 * So the work is split. The browser sends a MANIFEST of names and sizes with
 * the question, the planner picks a place to look, and this file does the
 * looking. Two rules make that arrangement safe, and both are load-bearing:
 *
 *   1. **Only names and sizes leave the browser.** buildManifest() returns
 *      table names, store ids, key names, row counts and byte lengths. It has
 *      no code path that reads a value, and AskController::localScope() drops
 *      anything else that arrives. These four stores hold the session, the CSRF
 *      token and a cached copy of the business; a question about what is cached
 *      must not be the request that uploads it.
 *
 *   2. **Values never leave this function.** searchLocalState() returns to the
 *      panel that displays it and nowhere else. Nothing is posted, and the
 *      answer is not narrated — the server has no rows to narrate.
 *
 * On top of that, anything that looks like a credential is masked before it is
 * shown. The values do stay in the browser, but a panel is a thing on a screen
 * in a shop, and a session cookie rendered in full is a session cookie in a
 * photograph. Name, size and shape are what answer "is this the row I mean";
 * devtools is what a developer who genuinely needs the bytes should open.
 *
 * Lives in services/ rather than in useAsk.js because useAsk.js imports
 * `@/echo.js`, which constructs a live Reverb connection at module scope — a
 * unit test of the read-only guarantee could not import it, and this guarantee
 * is exactly the kind the offline suite exists to pin down.
 */
import offlineDB from '@/indexedDB/offlineDB.js';
import { getActivePinia } from 'pinia';

/**
 * Names whose values are masked before display.
 *
 * Matched against the key, the cookie name or the field name — never against
 * the value, which would be a way of deciding a secret is not one because it
 * happens not to look like a token today.
 */
const SENSITIVE = /(token|secret|password|passwd|credential|session|csrf|xsrf|bearer|signature|private|api[_-]?key|access[_-]?key)/i;

/** How long a built manifest is reused for. */
const MANIFEST_TTL_MS = 15000;

let manifestCache = null;
let manifestCachedAt = 0;

const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

const byteLength = (text) => {
    const string = String(text ?? '');
    return encoder ? encoder.encode(string).length : string.length;
};

/**
 * JSON that cannot throw.
 *
 * Pinia state can hold a cycle, a DOM node or a class instance, and a manifest
 * or a search that dies on one of them takes the whole answer with it.
 */
const safeText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value !== 'object') return String(value);

    const seen = new WeakSet();

    try {
        return (
            JSON.stringify(value, (_key, inner) => {
                if (typeof inner === 'object' && inner !== null) {
                    if (seen.has(inner)) return '[circular]';
                    seen.add(inner);
                }
                if (typeof inner === 'function') return '[function]';
                if (inner instanceof Blob) return `[blob ${inner.size}B]`;
                return inner;
            }) ?? ''
        );
    } catch {
        return '[unreadable]';
    }
};

/** Cookie names and values, as far as a script can see them. */
const readCookies = () => {
    if (typeof document === 'undefined' || !document.cookie) return [];

    return document.cookie
        .split(';')
        .map((pair) => {
            const index = pair.indexOf('=');
            const name = (index === -1 ? pair : pair.slice(0, index)).trim();
            const value = index === -1 ? '' : pair.slice(index + 1);

            return name === '' ? null : { name, value };
        })
        .filter(Boolean);
};

/** Every localStorage key, with its value. */
const readLocalStorage = () => {
    const out = [];

    try {
        for (let i = 0; i < localStorage.length; i++) {
            const name = localStorage.key(i);
            if (name === null) continue;
            out.push({ name, value: localStorage.getItem(name) ?? '' });
        }
    } catch {
        // Private mode, or storage blocked by policy. An empty list is the
        // honest answer — the search reports it as one.
    }

    return out;
};

/** Every Pinia store this app has instantiated, with its top-level state. */
const readPiniaStores = () => {
    const state = getActivePinia()?.state?.value;

    if (!state || typeof state !== 'object') return [];

    // state.value rather than the internal _s map: it is what devtools and SSR
    // hydration read, it holds exactly the state (never the getters, which are
    // derived and would be recomputed here for nothing), and it does not depend
    // on a private field surviving the next Pinia major.
    return Object.entries(state).map(([name, value]) => ({ name, value: value ?? {} }));
};

// ---------------------------------------------------------------------------
// The manifest — names and sizes, and nothing else, ever
// ---------------------------------------------------------------------------

/**
 * What this device is holding, by name and size.
 *
 * Sent with a question so the planner can name a real store instead of guessing
 * at one. Read every line of this function before changing it: everything it
 * returns is put into a prompt and sent to a third-party model, so a `value`
 * added here is a value exported, and no caller downstream would notice.
 *
 * Memoised briefly because it counts every Dexie table and a person asking two
 * follow-ups in a row should not pay for that twice. Fifteen seconds is short
 * enough that a table filled by a sync in the meantime is named on the next
 * question rather than the one after it.
 */
export async function buildLocalManifest({ force = false } = {}) {
    const now = Date.now();

    if (!force && manifestCache && now - manifestCachedAt < MANIFEST_TTL_MS) {
        return manifestCache;
    }

    const manifest = {};

    try {
        const tables = await Promise.all(
            offlineDB.tables.map(async (table) => {
                try {
                    return { name: table.name, rows: await table.count() };
                } catch {
                    // One unreadable table must not cost the other forty.
                    return null;
                }
            }),
        );

        const named = tables.filter(Boolean);
        if (named.length) manifest.dexie = named;
    } catch {
        // Dexie closed, blocked, or never opened on this device.
    }

    try {
        const stores = readPiniaStores().map(({ name, value }) => ({
            name,
            keys: value && typeof value === 'object' ? Object.keys(value).slice(0, 24) : [],
        }));

        if (stores.length) manifest.pinia = stores;
    } catch {
        // No active Pinia — this ran outside the app.
    }

    const keys = readLocalStorage().map(({ name, value }) => ({ name, bytes: byteLength(value) }));
    if (keys.length) manifest.local_storage = keys;

    const cookies = readCookies().map(({ name }) => ({ name }));
    if (cookies.length) manifest.cookies = cookies;

    manifestCache = manifest;
    manifestCachedAt = now;

    return manifest;
}

/** Drops the memoised manifest, so the next question re-counts. */
export function forgetLocalManifest() {
    manifestCache = null;
    manifestCachedAt = 0;
}

// ---------------------------------------------------------------------------
// The search — values, and they stay here
// ---------------------------------------------------------------------------

const masked = (name) => SENSITIVE.test(String(name ?? ''));

/**
 * A short, readable stand-in for a value.
 *
 * Masked ones report their size and nothing else, which is enough to tell a
 * present token from an absent one without putting it on the screen.
 */
const preview = (name, value, maxLength) => {
    const text = safeText(value);

    if (masked(name)) {
        return { text: `•••••••• (${byteLength(text)} bytes, hidden)`, masked: true };
    }

    return {
        text: text.length > maxLength ? `${text.slice(0, maxLength)}…` : text,
        masked: false,
    };
};

const matches = (term, ...parts) => {
    if (!term) return true;
    const needle = term.toLowerCase();
    return parts.some((part) =>
        String(part ?? '')
            .toLowerCase()
            .includes(needle),
    );
};

/** The most name-like field on a cached row, for labelling it. */
const rowLabel = (row) => {
    for (const key of ['name', 'title', 'sale_number', 'invoice_number', 'code', 'sku', 'barcode', 'email', 'key']) {
        if (row?.[key]) return String(row[key]);
    }

    return row?.id !== undefined && row?.id !== null ? `#${row.id}` : '(row)';
};

/** The fields of a matched record worth showing: the ones asked for, or a sample. */
const recordDetail = (row, keys, maxLength) => {
    const chosen = keys.length ? keys : Object.keys(row ?? {}).slice(0, 6);
    const out = {};

    for (const key of chosen) {
        if (!(key in (row ?? {}))) continue;
        out[key] = preview(key, row[key], Math.max(24, Math.floor(maxLength / 2))).text;
    }

    return out;
};

async function searchDexie(instruction, remaining, report) {
    const { collection, search, keys, max_scan: maxScan, max_value_length: maxValue } = instruction;
    const entries = [];

    const tables = collection ? offlineDB.tables.filter((t) => t.name === collection) : offlineDB.tables;

    if (collection && tables.length === 0) {
        report.notes.push(`This device has no cached table called "${collection}".`);
        return entries;
    }

    for (const table of tables) {
        if (entries.length >= remaining) break;

        let rows = [];
        let total = 0;

        try {
            total = await table.count();
            // limit() rather than each(): a Dexie iteration cannot be broken
            // out of, and a full pass over a cached catalogue would lock the
            // tab for as long as it took.
            rows = await table.limit(maxScan).toArray();
        } catch {
            report.notes.push(`The "${table.name}" cache could not be read.`);
            continue;
        }

        // Reported whether or not it mattered, because a scan that stopped
        // early and one that finished look identical in the answer otherwise.
        report.scanned.push({ name: table.name, rows: total, scanned: rows.length, complete: rows.length >= total });

        for (const row of rows) {
            if (entries.length >= remaining) break;
            if (!matches(search, table.name, safeText(row))) continue;

            entries.push({
                store: 'dexie',
                collection: table.name,
                name: rowLabel(row),
                size: null,
                detail: recordDetail(row, keys, maxValue),
                masked: false,
            });
        }
    }

    return entries;
}

function searchPinia(instruction, remaining, report) {
    const { collection, search, keys, max_value_length: maxValue } = instruction;
    const entries = [];

    let stores = [];

    try {
        stores = readPiniaStores();
    } catch {
        report.notes.push('The app stores could not be read.');
        return entries;
    }

    if (collection) {
        stores = stores.filter((store) => store.name === collection);

        if (stores.length === 0) {
            report.notes.push(`This app has no store called "${collection}".`);
            return entries;
        }
    }

    for (const store of stores) {
        // One entry per state key rather than per store: "which store holds the
        // cart?" is answered by the key, and a whole store dumped as one blob
        // answers nothing.
        for (const [key, value] of Object.entries(store.value ?? {})) {
            if (entries.length >= remaining) break;
            if (keys.length && !keys.includes(key)) continue;
            if (!matches(search, store.name, key, safeText(value))) continue;

            const shown = preview(key, value, maxValue);

            entries.push({
                store: 'pinia',
                collection: store.name,
                name: key,
                size: byteLength(safeText(value)),
                detail: { value: shown.text },
                masked: shown.masked,
            });
        }
    }

    return entries;
}

function searchLocalStorage(instruction, remaining, report) {
    const { search, keys, max_value_length: maxValue } = instruction;
    const entries = [];

    const items = readLocalStorage();

    if (items.length === 0) {
        report.notes.push('This browser has nothing in local storage, or it is blocked here.');
    }

    for (const { name, value } of items) {
        if (entries.length >= remaining) break;
        if (keys.length && !keys.includes(name)) continue;
        if (!matches(search, name, value)) continue;

        const shown = preview(name, value, maxValue);

        entries.push({
            store: 'local_storage',
            collection: null,
            name,
            size: byteLength(value),
            detail: { value: shown.text },
            masked: shown.masked,
        });
    }

    return entries;
}

function searchCookies(instruction, remaining, report) {
    const { search, keys, max_value_length: maxValue } = instruction;
    const entries = [];

    // Said every time, not only when a cookie is missing. The session cookie is
    // HttpOnly by design and is the one people go looking for, and an answer
    // that quietly omits it reads as "there is no session cookie".
    report.notes.push('Cookies marked HttpOnly — the login session among them — are hidden from scripts and cannot be listed here.');

    for (const { name, value } of readCookies()) {
        if (entries.length >= remaining) break;
        if (keys.length && !keys.includes(name)) continue;
        if (!matches(search, name, value)) continue;

        const shown = preview(name, decodeURIComponent(value ?? ''), maxValue);

        entries.push({
            store: 'cookies',
            collection: null,
            name,
            size: byteLength(value),
            detail: { value: shown.text },
            masked: shown.masked,
        });
    }

    return entries;
}

/**
 * Carry out a local plan against this browser's own storage.
 *
 * `instruction` is `result_meta` from a completed local answer: the store, the
 * collection, the search text, the fields to show, and the caps the server
 * resolved. Re-run every time an answer is displayed rather than stored with
 * it — local state is a thing that is true now, and a saved copy of what a
 * cache held last Tuesday would be worse than no answer at all.
 */
export async function searchLocalState(instruction) {
    const plan = {
        store: instruction?.store ?? 'all',
        collection: instruction?.collection || null,
        search: (instruction?.search ?? '').trim(),
        keys: Array.isArray(instruction?.keys) ? instruction.keys : [],
        limit: Math.max(1, Number(instruction?.limit ?? 25)),
        max_scan: Math.max(1, Number(instruction?.max_scan ?? 2000)),
        max_value_length: Math.max(16, Number(instruction?.max_value_length ?? 200)),
    };

    const report = { notes: [], scanned: [] };
    const entries = [];

    const stores = plan.store === 'all' ? ['dexie', 'pinia', 'local_storage', 'cookies'] : [plan.store];

    for (const store of stores) {
        const remaining = plan.limit - entries.length;
        if (remaining <= 0) break;

        // A collection belongs to one store, so it is only meaningful for the
        // store it names — "all" ignores it rather than matching a Pinia store
        // id against a Dexie table name.
        const scoped = { ...plan, collection: plan.store === 'all' ? null : plan.collection };

        if (store === 'dexie') entries.push(...(await searchDexie(scoped, remaining, report)));
        if (store === 'pinia') entries.push(...searchPinia(scoped, remaining, report));
        if (store === 'local_storage') entries.push(...searchLocalStorage(scoped, remaining, report));
        if (store === 'cookies') entries.push(...searchCookies(scoped, remaining, report));
    }

    if (entries.some((entry) => entry.masked)) {
        report.notes.push('Values that name a token, a session or a password are hidden rather than shown.');
    }

    const partial = report.scanned.filter((row) => !row.complete);

    if (partial.length) {
        report.notes.push(
            `Stopped after ${plan.max_scan.toLocaleString()} rows in ${partial.map((row) => row.name).join(', ')} — there may be more.`,
        );
    }

    return {
        store: plan.store,
        collection: plan.collection,
        search: plan.search,
        entries: entries.slice(0, plan.limit),
        truncated: entries.length >= plan.limit,
        scanned: report.scanned,
        notes: report.notes,
    };
}
